import { Check } from 'lucide-react'
import type { LifecycleStage, ShipmentLifecycle, StageState } from './types'

// Hand-rolled horizontal stepper — there is no shadcn `stepper`/`progress`
// primitive. Colors mirror the app's booking-status / review-outcome maps.
const DOT_CLASS: Record<StageState, string> = {
  done: 'border-emerald-500 bg-emerald-500 text-white',
  // Hollow (white center, blue ring) so "current / in progress" reads as
  // distinct from the solid filled "done" dot beside it.
  current: 'border-blue-600 bg-white text-blue-600 ring-4 ring-blue-600/15',
  attention: 'border-amber-500 bg-amber-500 text-white ring-4 ring-amber-500/15',
  blocked: 'border-red-500 bg-red-500 text-white ring-4 ring-red-500/15',
  expected: 'border-dashed border-blue-500 bg-blue-50 text-blue-600',
  upcoming: 'border-gray-300 bg-white text-gray-400',
}

const LABEL_CLASS: Record<StageState, string> = {
  done: 'text-gray-900',
  current: 'text-blue-700 font-semibold',
  attention: 'text-amber-700 font-semibold',
  blocked: 'text-red-700 font-semibold',
  expected: 'text-blue-600',
  upcoming: 'text-gray-400',
}

const ACTIVE_STATES: ReadonlySet<StageState> = new Set(['current', 'attention', 'blocked'])

// The connector leading INTO a step is "filled" when the previous step is reached.
function connectorClass(prev: LifecycleStage | undefined): string {
  if (!prev) return ''
  if (prev.state === 'done') return 'bg-emerald-500'
  if (prev.state === 'expected') return 'bg-blue-400'
  return 'bg-gray-200'
}

function dotGlyph(state: StageState) {
  if (state === 'done') return <Check className="h-3 w-3" strokeWidth={3} />
  if (state === 'blocked' || state === 'attention') return <span className="text-[11px] font-extrabold leading-none">!</span>
  if (state === 'expected') return <span className="text-[11px] font-extrabold leading-none">~</span>
  return null
}

export function StageRail({
  stages,
  size = 'default',
  className = '',
}: {
  stages: LifecycleStage[]
  size?: 'default' | 'compact'
  className?: string
}) {
  if (stages.length === 0) return null
  const compact = size === 'compact'
  const dotSize = compact ? 'h-4 w-4' : 'h-6 w-6'
  const connectorTop = compact ? 'top-[7px]' : 'top-[11px]'
  const topPad = compact ? 'pt-5' : 'pt-7'
  const labelSize = compact ? 'text-[10px]' : 'text-[11px]'

  return (
    <ol className={`flex items-start ${className}`} aria-label="Shipment lifecycle">
      {stages.map((stage, index) => {
        const prev = index > 0 ? stages[index - 1] : undefined
        const active = ACTIVE_STATES.has(stage.state)
        return (
          <li
            key={stage.key}
            className={`relative min-w-0 flex-1 text-center ${topPad}`}
            aria-current={active ? 'step' : undefined}
          >
            {prev && (
              <span className={`absolute ${connectorTop} right-1/2 h-0.5 w-full ${connectorClass(prev)}`} aria-hidden="true" />
            )}
            <span
              className={`absolute left-1/2 top-0 z-10 flex ${dotSize} -translate-x-1/2 items-center justify-center rounded-full border-2 ${DOT_CLASS[stage.state]}`}
            >
              {dotGlyph(stage.state)}
            </span>
            <span className={`block ${labelSize} leading-tight ${LABEL_CLASS[stage.state]}`}>{stage.label}</span>
          </li>
        )
      })}
    </ol>
  )
}

// Macro rail for the shipment header, including the per-booking breakdown when
// active bookings diverge ("Mixed").
export function ShipmentStageRail({ lifecycle }: { lifecycle: ShipmentLifecycle }) {
  const showBreakdown = lifecycle.mixed && lifecycle.per_booking.length > 1

  return (
    <div className="rounded-lg border bg-white p-4">
      <div className="flex items-center gap-2">
        <StageRail stages={lifecycle.stages} className="flex-1" />
        {lifecycle.mixed && (
          <span className="shrink-0 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700">
            Mixed
          </span>
        )}
      </div>
      {showBreakdown && (
        <div className="mt-3 space-y-2 border-t pt-3">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Per booking</div>
          {lifecycle.per_booking.map((perBooking) => (
            <div
              key={perBooking.booking_ref.booking_id}
              className="flex items-center gap-3 rounded-md border bg-gray-50/60 px-3 py-1.5"
            >
              <div className="w-24 shrink-0 truncate font-mono text-[11px] font-semibold text-blue-600">
                {perBooking.booking_ref.internal_reference}
              </div>
              <StageRail stages={perBooking.stages} size="compact" className="flex-1" />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
